import { type InitContext } from "@btnet/services-btnet/handlers/models/initContext";

import {
    normalizeAnalyticsConfig,
    publishAnalyticsConfig,
} from "utilities/analytics/analyticsConfig";

/**
 * Bootstrap ("init") configuration the SPA needs before it renders — Datadog RUM, Sentry, and
 * analytics (Mixpanel/Sawmill) settings — fetched from the anonymous, cacheable APIX endpoint
 * (`GET /apix/v2/context/init`) so the app can start up without the render-blocking, session-aware
 * JScriptGlobalsHandler.ashx.
 *
 * The request is kicked off by an inline script in index.html (see `__initContextPromise__`) so it
 * overlaps the app-bundle download rather than waiting behind it. The entry then awaits that promise
 * before initializing observability and rendering. See ADO 282448.
 *
 * The shape is the orval-generated {@link InitContext} — the single source of truth for the
 * wire contract. `environment` (Datadog env / Sentry environment) is intentionally not part of the
 * payload — the frontend derives it from the hostname in `utilities/environment/environment.ts`.
 * The release identifier is likewise not part of it: this endpoint is cached for up to a day, so a
 * runtime-fetched release could tag telemetry with the previous deploy. It is stamped into the
 * bundle at build time (`VITE_BUILD_ID`, read in `index-app.tsx`). See ADO 285393.
 */

/** A number within [min, max], otherwise the conservative fallback (defaults to 0). */
function clampedNumber(value: unknown, min: number, max: number, fallback = 0): number {
    return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
        ? value
        : fallback;
}

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function asBoolean(value: unknown): boolean {
    return value === true;
}

/** Observability fully disabled — used when the init request fails or returns garbage. */
const DISABLED_OBSERVABILITY = {
    datadog: {
        enabled: false,
        applicationId: "",
        clientToken: "",
        site: "",
        sessionSampleRate: 0,
        sessionReplaySampleRate: 0,
        traceSampleRate: 0,
        trackUserInteractions: false,
        enableTracing: false,
    },
    sentry: { enabled: false, dsn: "", tracesSampleRate: 0 },
};

/** Observability and analytics both disabled. */
function disabledConfig(): InitContext {
    return { ...DISABLED_OBSERVABILITY, analytics: normalizeAnalyticsConfig(undefined) };
}

/**
 * Validates a raw init-context payload (from the API) into a safe {@link InitContext}.
 *
 * Never throws. Missing, malformed, or out-of-range values fail safe:
 * - an integration is `enabled` only when its own flag is true AND its required identifiers
 *   (Datadog applicationId + clientToken, Sentry dsn) are present;
 * - out-of-range or non-numeric sample rates fall back to 0 (no telemetry) rather than
 *   silently enabling an unexpectedly high sample rate.
 *
 * Analytics normalizes separately — see {@link normalizeAnalyticsConfig}.
 */
export function normalizeInitContext(raw: unknown): InitContext {
    if (raw === null || typeof raw !== "object") {
        return disabledConfig();
    }
    const root = raw as Record<string, unknown>;
    const dd = (root.datadog ?? {}) as Record<string, unknown>;
    const sn = (root.sentry ?? {}) as Record<string, unknown>;

    const ddApplicationId = asString(dd.applicationId);
    const ddClientToken = asString(dd.clientToken);
    const sentryDsn = asString(sn.dsn);

    return {
        datadog: {
            enabled: asBoolean(dd.enabled) && !!ddApplicationId && !!ddClientToken,
            applicationId: ddApplicationId,
            clientToken: ddClientToken,
            site: asString(dd.site),
            sessionSampleRate: clampedNumber(dd.sessionSampleRate, 0, 100),
            sessionReplaySampleRate: clampedNumber(dd.sessionReplaySampleRate, 0, 100),
            traceSampleRate: clampedNumber(dd.traceSampleRate, 0, 100),
            trackUserInteractions: asBoolean(dd.trackUserInteractions),
            enableTracing: asBoolean(dd.enableTracing),
        },
        sentry: {
            enabled: asBoolean(sn.enabled) && !!sentryDsn,
            dsn: sentryDsn,
            tracesSampleRate: clampedNumber(sn.tracesSampleRate, 0, 1),
        },
        analytics: normalizeAnalyticsConfig(root.analytics),
    };
}

/**
 * Awaits the in-flight init request started by index.html and validates the result. Resolves to a
 * fully-disabled config if the request failed, the promise is absent (e.g. tests, storybook), or the
 * payload is malformed — observability never blocks or breaks app startup.
 *
 * Publishes the analytics section for `analytics.ts`, which reads it lazily rather than importing
 * this module's awaited bootstrap. See `utilities/analytics/analyticsConfig`.
 */
export async function loadInitContext(): Promise<InitContext> {
    const context = await resolveInitContext();
    publishAnalyticsConfig(context.analytics);
    return context;
}

async function resolveInitContext(): Promise<InitContext> {
    const promise = window.__initContextPromise__;
    if (!promise) {
        return disabledConfig();
    }
    try {
        return normalizeInitContext(await promise);
    } catch {
        return disabledConfig();
    }
}
